import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocale } from '../contexts/LocaleContext';
import { useAppData } from '../contexts/AppDataContext';
import { tenantCompanyService } from '../services/tenantCompanyService';
import { csvImportService } from '../services/csvImportService';
import { useTenantMapData } from '../hooks/useTenantMapData';
import {
  TENANT_NODE_TABS,
  TENANT_DATA_HUB_TAB,
  TENANT_DATA_IMPORT_SECTION,
  isDataImportSection,
  getNodeTabByPageTab,
} from '../config/tenantNodeTabsConfig';
import { computeProfileCompletion } from '../utils/tenantMapModel';
import { buildExistingImportByKey } from '../utils/tenantCsvImportStatus';
import TenantCompanyProfileHero from '../components/TenantCompany/TenantCompanyProfileHero';
import TenantCompanyPageTabs from '../components/TenantCompany/TenantCompanyPageTabs';
import TenantCompanyOnboardingBanner from '../components/TenantCompany/TenantCompanyOnboardingBanner';
import CsvMapperModal from '../components/TenantCompany/CsvMapperModal';

const createStatusMap = (keys) =>
  keys.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});

const NODE_KEYS = TENANT_NODE_TABS.map((tab) => tab.key);
const IMPORT_KEYS = Object.values(csvImportService.CSV_IMPORT_KEYS);
const DEFAULT_DATA_SECTION = TENANT_NODE_TABS[0]?.pageTab;

function MyTenantCompany() {
  const { t } = useLocale();
  const {
    tenantProfile,
    tenantNodesByKey,
    tenantSuppliers,
    companies,
    tenantCompanyRoutes,
    tenantLoading,
    tenantDetailsLoading,
    tenantLoaded,
    tenantMapReady,
    refreshTenantCompany,
    patchTenantNodesByKey,
    setTenantProfile,
  } = useAppData();

  const [savingProfile, setSavingProfile] = useState(false);
  const [loadingByKey, setLoadingByKey] = useState(() => createStatusMap(NODE_KEYS));
  const [savingByKey, setSavingByKey] = useState(() => createStatusMap(NODE_KEYS));
  const [uploadingByKey, setUploadingByKey] = useState(() => createStatusMap(IMPORT_KEYS));
  const [resultsByKey, setResultsByKey] = useState({});
  const [mapperOpen, setMapperOpen] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [activePrimaryTab, setActivePrimaryTab] = useState('map');
  const [activeDataSection, setActiveDataSection] = useState(DEFAULT_DATA_SECTION);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [overallRiskScore, setOverallRiskScore] = useState(null);
  const [savingSuppliers, setSavingSuppliers] = useState(false);

  const nodesByKeyRef = useRef(tenantNodesByKey);
  nodesByKeyRef.current = tenantNodesByKey;
  const dataHubSyncedRef = useRef(false);

  const profile = tenantProfile;
  const hasTenantProfile = Boolean(tenantProfile);
  const nodesByKey = tenantNodesByKey;

  const mapData = useTenantMapData(nodesByKey, {
    companyRoutes: tenantCompanyRoutes,
    enableGeocoding: false,
    enableSupplierRoutes: false,
  });

  const loadNode = useCallback(
    async (key, { force = false } = {}) => {
      if (!force && nodesByKeyRef.current[key] !== undefined) {
        return;
      }

      setLoadingByKey((prev) => ({ ...prev, [key]: true }));
      const response = await tenantCompanyService.getTenantNodes(undefined, key);
      if (response.success) {
        patchTenantNodesByKey(key, Array.isArray(response.data) ? response.data : []);
      } else {
        toast.error(response.message || t('tenantLoadNodesFailed'));
      }
      setLoadingByKey((prev) => ({ ...prev, [key]: false }));
    },
    [patchTenantNodesByKey, t]
  );

  const loadLatestTenantRisk = useCallback(async () => {
    if (!hasTenantProfile) {
      setOverallRiskScore(null);
      return;
    }
    const response = await tenantCompanyService.getLatestTenantRiskAnalysis();
    if (response.success && response.data?.tenantOverallRiskScore != null) {
      setOverallRiskScore(response.data.tenantOverallRiskScore);
    } else if (response.success && !response.data) {
      setOverallRiskScore(null);
    }
  }, [hasTenantProfile]);

  useEffect(() => {
    if (activePrimaryTab !== 'map' && activePrimaryTab !== 'analysis' && activePrimaryTab !== 'overview') {
      return;
    }
    if (!hasTenantProfile) {
      setOverallRiskScore(null);
      return;
    }
    loadLatestTenantRisk();
  }, [activePrimaryTab, hasTenantProfile, loadLatestTenantRisk]);

  useEffect(() => {
    if (activePrimaryTab !== 'overview') return;
    NODE_KEYS.forEach((key) => {
      loadNode(key);
    });
  }, [activePrimaryTab, loadNode]);

  useEffect(() => {
    if (activePrimaryTab !== TENANT_DATA_HUB_TAB) {
      dataHubSyncedRef.current = false;
      return;
    }

    if (!dataHubSyncedRef.current) {
      dataHubSyncedRef.current = true;
      NODE_KEYS.forEach((key) => {
        loadNode(key);
      });
      return;
    }

    if (isDataImportSection(activeDataSection)) {
      return;
    }

    const tab = getNodeTabByPageTab(activeDataSection);
    if (tab) {
      loadNode(tab.key);
    }
  }, [activePrimaryTab, activeDataSection, loadNode]);

  const handleTenantAnalysisComplete = useCallback((result) => {
    if (result?.tenantOverallRiskScore != null) {
      setOverallRiskScore(result.tenantOverallRiskScore);
    }
  }, []);

  const handleSuppliersChange = useCallback(async () => {
    setSavingSuppliers(true);
    await refreshTenantCompany();
    setSavingSuppliers(false);
  }, [refreshTenantCompany]);

  const navigateTenant = useCallback(({ primary, dataSection }) => {
    if (primary) setActivePrimaryTab(primary);
    if (dataSection !== undefined) setActiveDataSection(dataSection);
  }, []);

  const profileValue = useMemo(() => profile || {}, [profile]);
  const existingImportByKey = useMemo(
    () =>
      buildExistingImportByKey({
        profile: profileValue,
        hasTenantProfile,
        nodesByKey,
        tenantSuppliers,
      }),
    [profileValue, hasTenantProfile, nodesByKey, tenantSuppliers]
  );
  const completion = useMemo(
    () => computeProfileCompletion(profileValue, nodesByKey),
    [profileValue, nodesByKey]
  );

  const isTenantBootstrapping = tenantLoading || tenantDetailsLoading || !tenantLoaded;
  const showOnboardingBanner =
    !isTenantBootstrapping &&
    !tenantMapReady &&
    !mapData.hasMapContent &&
    (!hasTenantProfile || completion < 60);

  const handleSaveProfile = async (payload) => {
    setSavingProfile(true);
    const response = await tenantCompanyService.upsertTenantCompany(undefined, payload);
    if (response.success) {
      setTenantProfile(response.data || payload);
      toast.success(t('tenantProfileSaved'));
    } else {
      toast.error(response.message || t('tenantSaveProfileFailed'));
    }
    setSavingProfile(false);
  };

  const handleSaveNodeRows = async (nodeKey, rows) => {
    if (!Array.isArray(rows)) {
      toast.error(t('tenantRowsInvalidJson'));
      return;
    }
    setSavingByKey((prev) => ({ ...prev, [nodeKey]: true }));
    const response = await tenantCompanyService.upsertTenantNodes(undefined, nodeKey, rows);
    if (response.success) {
      patchTenantNodesByKey(
        nodeKey,
        Array.isArray(response.data) ? response.data : rows
      );
      tenantCompanyService.invalidateGraphContextCache();
      toast.success(t('tenantRowsSaved'));
    } else {
      toast.error(response.message || t('tenantSaveNodesFailed'));
    }
    setSavingByKey((prev) => ({ ...prev, [nodeKey]: false }));
  };

  const handleUploadRequest = (importKey, file) => {
    setPendingUpload({ importKey, file });
    setMapperOpen(true);
  };

  const handleMappedUpload = async ({ importKey: mappedImportKey, mappedRows }) => {
    const importKey = mappedImportKey || pendingUpload?.importKey;
    if (!importKey || !Array.isArray(mappedRows) || mappedRows.length === 0) {
      return;
    }

    setMapperOpen(false);
    setUploadingByKey((prev) => ({ ...prev, [importKey]: true }));
    const response = await csvImportService.uploadMappedRows(importKey, mappedRows);

    if (response.success) {
      const result = response.data || {};
      setResultsByKey((prev) => ({ ...prev, [importKey]: result }));
      const failedCount = Number(result.failed ?? 0);
      const createdCount = Number(result.created ?? 0);
      const updatedCount = Number(result.updated ?? 0);
      if (failedCount > 0 && createdCount === 0 && updatedCount === 0) {
        toast.error(t('tenantCsvUploadFailed'));
      } else if (failedCount > 0) {
        toast.error(
          t('tenantCsvUploadPartial', {
            created: createdCount,
            updated: updatedCount,
            failed: failedCount,
          })
        );
      } else {
        toast.success(t('tenantCsvUploadSuccess'));
      }

      tenantCompanyService.invalidateGraphContextCache();
      await refreshTenantCompany();
    } else {
      setResultsByKey((prev) => ({ ...prev, [importKey]: response.data || {} }));
      toast.error(response.message || t('tenantCsvUploadFailed'));
    }

    setUploadingByKey((prev) => ({ ...prev, [importKey]: false }));
    setPendingUpload(null);
  };

  const handleMapperClose = () => {
    setMapperOpen(false);
    setPendingUpload(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <TenantCompanyOnboardingBanner
        visible={showOnboardingBanner}
        onGoToOverview={() => navigateTenant({ primary: 'overview' })}
        onGoToImport={() =>
          navigateTenant({ primary: TENANT_DATA_HUB_TAB, dataSection: TENANT_DATA_IMPORT_SECTION })
        }
      />

      <TenantCompanyProfileHero
        profile={profileValue}
        nodesByKey={nodesByKey}
        supplierCount={tenantSuppliers.length}
        loading={tenantDetailsLoading}
        overallRiskScore={overallRiskScore}
        onNavigate={navigateTenant}
      />

      <TenantCompanyPageTabs
        activePrimaryTab={activePrimaryTab}
        onPrimaryTabChange={setActivePrimaryTab}
        activeDataSection={activeDataSection}
        onDataSectionChange={setActiveDataSection}
        onNavigate={navigateTenant}
        profile={profileValue}
        hasTenantProfile={hasTenantProfile}
        nodesByKey={nodesByKey}
        tenantSuppliers={tenantSuppliers}
        companies={companies || []}
        loadingSuppliers={tenantDetailsLoading}
        savingSuppliers={savingSuppliers}
        loadingProfile={tenantDetailsLoading}
        savingProfile={savingProfile}
        loadingByKey={loadingByKey}
        savingByKey={savingByKey}
        uploadingByKey={uploadingByKey}
        resultsByKey={resultsByKey}
        existingImportByKey={existingImportByKey}
        mapData={mapData}
        mapDataLoading={isTenantBootstrapping && !mapData.hasMapContent}
        mapEnhancing={mapData.geocoding}
        selectedMarkerId={selectedMarkerId}
        onMarkerSelect={setSelectedMarkerId}
        overallRiskScore={overallRiskScore}
        onSaveProfile={handleSaveProfile}
        onSaveNodeRows={handleSaveNodeRows}
        onUploadFromPanel={handleUploadRequest}
        onSuppliersChange={handleSuppliersChange}
        onTenantAnalysisComplete={handleTenantAnalysisComplete}
      />

      <CsvMapperModal
        open={mapperOpen}
        importKey={pendingUpload?.importKey}
        file={pendingUpload?.file}
        onClose={handleMapperClose}
        onConfirm={handleMappedUpload}
      />
    </div>
  );
}

export default MyTenantCompany;
